import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Spinner } from '@creditornot/cb-progress';
import React, { useState } from 'react';
import { Switch, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';

import { RouteWhenAuthenticated } from 'common/components';
import { useExternalAccessRights } from 'common/hooks';
import { usePermissions } from 'common/wolt-permissions';
import * as permissionUtils from 'common/wolt-permissions/permissionUtils';
import { PageHeader, useExperienceModuleContext } from 'modules/experience-core';
import { VenueListingManagerRoot } from 'modules/shared-menu/components/VenueListingManagerRoot';
import { TRANSLATIONS_PAGE_PATH } from 'modules/shared-menu/constants';
import { ScopeContextProvider } from 'modules/shared-menu/contexts/ScopeContext';
import { useIsListingManagerEnabled } from 'modules/shared-menu/hooks/useIsListingManagerEnabled';
import { MenuDevTools } from 'modules/shared-menu/pages/VenueLevelMenuEditorPage/components/MenuDevTools';
import { VenueTranslationRoot } from 'modules/translation/components/VenueTranslationRoot';

import { ClassicMenuEditorPage } from './ClassicMenuEditorPage';
import { useIsListingManagerDevtoolsEnabled } from '../../shared-menu/hooks/useIsListingManagerDevtoolsEnabled';
import { MenuModuleContextProvider, useMenuModuleContext } from '../context';
import { useIsVenueLevelMenuEditorEnabled } from '../hooks/useIsVenueLevelMenuEditorEnabled';
import { useSharedMenuLink } from '../hooks/useSharedMenuLink';

const SpinnerContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${cbSemanticCssVars.surfaceMain};
`;

const isProdEnv = process.env.APP_ENV === 'production';
const isDevEnv = process.env.APP_ENV === 'development';

const MenuEditorRoutes = () => {
  const { path } = useRouteMatch();
  const { venue } = useMenuModuleContext();

  const { sharedMenuLink, isSharedMenuLinkLoading } = useSharedMenuLink();
  const { isVenueLevelMenuEditorEnabled, isVenueLevelMenuEditorEnabledLoading } = useIsVenueLevelMenuEditorEnabled();
  const { isListingManagerBetaEnabled, isListingManagerEnabledLoading } = useIsListingManagerEnabled(venue);
  const isListingManagerDevtoolsEnabled = useIsListingManagerDevtoolsEnabled();
  const [forceClassicMenuRender, setForceClassicMenuRender] = useState(false);
  const [forceVlmaRender, setForceVlmaRender] = useState(false);
  const permissions = usePermissions();
  const externalAccessRights = useExternalAccessRights();

  if (isSharedMenuLinkLoading || isVenueLevelMenuEditorEnabledLoading || isListingManagerEnabledLoading) {
    return (
      <SpinnerContainer>
        <Spinner size="large" color="blue" />
      </SpinnerContainer>
    );
  }

  const notSharedVenue = !sharedMenuLink?.selection_is_shared;

  const isVlmaEnabled =
    isVenueLevelMenuEditorEnabled ||
    (isDevEnv && isListingManagerBetaEnabled) ||
    (isProdEnv && isListingManagerBetaEnabled && venue.product_line === 'restaurant' && notSharedVenue);

  const shouldUseVlma = isVlmaEnabled && (!!sharedMenuLink?.selection_id || !venue.menu);

  const isRenderingVlma = (shouldUseVlma && !forceClassicMenuRender) || forceVlmaRender;

  return (
    <MenuModuleContextProvider value={{ venue }}>
      <PageHeader />
      <Switch>
        {permissionUtils.hasTranslationPermission(permissions, externalAccessRights, venue) && (
          <RouteWhenAuthenticated exact path={`${path}/${TRANSLATIONS_PAGE_PATH}`} component={VenueTranslationRoot} />
        )}
        <RouteWhenAuthenticated path={path}>
          {isListingManagerDevtoolsEnabled && (
            <MenuDevTools
              setForceClassicMenuRender={setForceClassicMenuRender}
              setForceVlmaRender={setForceVlmaRender}
              isRenderingVlma={isRenderingVlma}
              venueLink={sharedMenuLink}
            />
          )}
          {isRenderingVlma ? (
            <ScopeContextProvider value={{ id: venue.id, type: 'venue' }}>
              <VenueListingManagerRoot selectionId={sharedMenuLink?.selection_id ?? ''} />
            </ScopeContextProvider>
          ) : (
            <ClassicMenuEditorPage />
          )}
        </RouteWhenAuthenticated>
      </Switch>
    </MenuModuleContextProvider>
  );
};

/**
 * This component decides between rendering the classic menu editor (en route to be deprecated) or the new experience, which is the Venue Level Menu Editor (VLME), based on the Shared Menu Editor
 */
export const ExperienceMenu = () => {
  const { selectedExperienceVenueEntity } = useExperienceModuleContext();
  if (!selectedExperienceVenueEntity) {
    return null;
  }
  const venue = selectedExperienceVenueEntity.experienceEntityObject;

  return (
    <MenuModuleContextProvider value={{ venue }}>
      <MenuEditorRoutes />
    </MenuModuleContextProvider>
  );
};
